import { useMemo } from 'react';

import { QuestionStep } from '../QuestionStep';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  contrastRatio,
  nudgeForContrast,
  WCAG_AA_NORMAL,
} from '@/lib/contrast';
import {
  PALETTE_MODE_OPTIONS,
  PRESET_PALETTES,
  DEFAULT_PALETTE,
  DEFAULT_DARK_PALETTE,
  STEPS,
  type ColorPalette,
  type PaletteMode,
  type QuestionnaireAnswers,
} from '../questions';

interface ColorStepProps {
  answers: QuestionnaireAnswers;
  onChange: (next: Partial<QuestionnaireAnswers>) => void;
}

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function ColorStep({ answers, onChange }: ColorStepProps) {
  const step = STEPS.find((s) => s.id === 'color')!;
  const mode: PaletteMode = answers.palette_mode ?? 'light';
  const palette = answers.palette ?? (mode === 'dark' ? DEFAULT_DARK_PALETTE : DEFAULT_PALETTE);

  // Filter presets to whichever mode is selected; users only see options
  // valid for their current "feel".
  const visiblePresets = useMemo(
    () => PRESET_PALETTES.filter((p) => p.mode === mode),
    [mode],
  );

  // Real-time contrast between primary (text) and secondary (background).
  const contrast = useMemo(
    () => contrastRatio(palette.primary, palette.secondary),
    [palette.primary, palette.secondary],
  );
  const passing = contrast !== null && contrast >= WCAG_AA_NORMAL;

  // Pre-compute the auto-fix suggestion so the button can show the target
  // ratio. Only computed when the current pair fails AA — cheap regardless.
  const suggestion = useMemo(() => {
    if (passing) return null;
    return nudgeForContrast(palette.primary, palette.secondary, WCAG_AA_NORMAL);
  }, [palette.primary, palette.secondary, passing]);

  const setMode = (next: PaletteMode) => {
    if (next === mode) return;
    // Switching mode: if the user was on a preset, swap them to the same-
    // -named preset in the other mode if one exists; otherwise drop in
    // the mode's default. We never silently corrupt their hand-picked
    // palette.
    const currentName = PRESET_PALETTES.find(
      (p) =>
        p.palette.primary === palette.primary &&
        p.palette.secondary === palette.secondary &&
        p.palette.accent === palette.accent &&
        p.palette.detail === palette.detail,
    )?.name;
    const replacement = currentName
      ? PRESET_PALETTES.find((p) => p.name === currentName && p.mode === next)
      : undefined;
    onChange({
      palette_mode: next,
      palette:
        replacement?.palette ??
        (next === 'dark' ? DEFAULT_DARK_PALETTE : DEFAULT_PALETTE),
    });
  };

  const setKey = (key: keyof ColorPalette, value: string) => {
    onChange({ palette: { ...palette, [key]: value } });
  };

  const isPresetMatch = (p: ColorPalette) =>
    p.primary === palette.primary &&
    p.secondary === palette.secondary &&
    p.accent === palette.accent &&
    p.detail === palette.detail;

  const applySuggestion = () => {
    if (!suggestion) return;
    if (suggestion.movedKey === 'background') {
      setKey('secondary', suggestion.adjusted);
    } else {
      setKey('primary', suggestion.adjusted);
    }
  };

  return (
    <QuestionStep step={step}>
      <div className="grid gap-10">
        {/* Mode toggle */}
        <div>
          <p className="smallcaps mb-3 text-graphite">Mode</p>
          <div
            role="radiogroup"
            aria-label="Palette mode"
            className="inline-flex rounded-sm border border-ink/15 bg-paper p-1"
          >
            {PALETTE_MODE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={mode === opt.value}
                onClick={() => setMode(opt.value)}
                className={cn(
                  'rounded-sm px-4 py-1.5 text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre',
                  mode === opt.value
                    ? 'bg-ink text-paper'
                    : 'text-ink/80 hover:text-ink',
                )}
              >
                <span className="font-medium">{opt.label}</span>
                <span className="ml-2 text-[11px] text-current/70">{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Presets */}
        <div>
          <p className="smallcaps mb-3 text-graphite">Presets</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visiblePresets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => onChange({ palette: preset.palette })}
                className={cn(
                  'group flex items-center gap-4 rounded-sm border bg-paper p-3 text-left transition-colors',
                  isPresetMatch(preset.palette) ? 'border-ink' : 'border-ink/15 hover:border-ink/40',
                )}
                aria-pressed={isPresetMatch(preset.palette)}
              >
                <div className="flex shrink-0 gap-1">
                  <Swatch color={preset.palette.primary} />
                  <Swatch color={preset.palette.secondary} />
                  <Swatch color={preset.palette.accent} />
                  <Swatch color={preset.palette.detail} />
                </div>
                <span className="font-serif text-base italic text-ink">{preset.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Manual */}
        <div>
          <p className="smallcaps mb-3 text-graphite">Or set by hand</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ColorField label="Primary (text)"        value={palette.primary}   onChange={(v) => setKey('primary', v)} />
            <ColorField label="Secondary (background)" value={palette.secondary} onChange={(v) => setKey('secondary', v)} />
            <ColorField label="Accent"                value={palette.accent}    onChange={(v) => setKey('accent', v)} />
            <ColorField label="Detail"                value={palette.detail}    onChange={(v) => setKey('detail', v)} />
          </div>
        </div>

        {/* Contrast badge + auto-fix */}
        <ContrastBadge
          ratio={contrast}
          passing={passing}
          suggestion={suggestion}
          onApply={applySuggestion}
        />

        {/* Optional palette URL */}
        <div className="grid max-w-xl gap-2">
          <Label htmlFor="palette-url">
            Or paste a palette URL <span className="text-graphite text-xs">(coolors.co, etc — optional)</span>
          </Label>
          <Input
            id="palette-url"
            type="url"
            value={answers.palette_links ?? ''}
            onChange={(e) => onChange({ palette_links: e.target.value })}
            placeholder="https://coolors.co/..."
          />
        </div>
      </div>
    </QuestionStep>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className="inline-block h-6 w-6 rounded-sm border border-ink/15"
      style={{ background: color }}
    />
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const valid = HEX_RE.test(value);
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={valid ? expandHex(value) : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-10 cursor-pointer rounded-sm border border-ink/20 bg-transparent"
          aria-label={`${label} colour picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className={cn('font-mono uppercase', !valid && 'border-vermilion/60')}
          maxLength={7}
        />
      </div>
    </div>
  );
}

interface ContrastBadgeProps {
  ratio: number | null;
  passing: boolean;
  suggestion: ReturnType<typeof nudgeForContrast>;
  onApply: () => void;
}

function ContrastBadge({ ratio, passing, suggestion, onApply }: ContrastBadgeProps) {
  if (ratio === null) {
    return (
      <p className="text-xs text-graphite">
        Contrast can&apos;t be measured — make sure both primary and secondary are valid hex codes.
      </p>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-sm border p-3',
        passing
          ? 'border-emerald-700/40 bg-emerald-50/40'
          : 'border-vermilion/40 bg-vermilion/5',
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'font-mono text-[11px] uppercase tracking-wider',
            passing ? 'text-emerald-800' : 'text-vermilion',
          )}
        >
          {passing ? 'Passes WCAG AA' : 'Below WCAG AA'}
        </span>
        <span className="text-sm tabular-nums text-ink">
          Primary on Secondary &mdash; <strong>{ratio.toFixed(2)}:1</strong>
          <span className="ml-1 text-graphite">/ target {WCAG_AA_NORMAL}:1</span>
        </span>
      </div>

      {!passing && suggestion ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onApply}
          className="font-mono text-[11px]"
        >
          Use compliant version
          <span className="ml-2 inline-flex items-center gap-1">
            <Swatch color={suggestion.adjusted} />
            <span className="tabular-nums">→ {suggestion.ratioAfter.toFixed(2)}:1</span>
          </span>
        </Button>
      ) : null}
    </div>
  );
}

function expandHex(hex: string): string {
  const clean = hex.replace('#', '');
  if (clean.length === 3) {
    return '#' + clean.split('').map((c) => c + c).join('');
  }
  return '#' + clean.padEnd(6, '0').slice(0, 6);
}
